/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import AudioTrigger from './AudioTrigger';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Represents a group of [[cc.AudioSource]]s that are triggered off
 * of the same cue.
 */
@cc._decorator.ccclass
export default class AudioTriggerGroup extends AudioTrigger
{
    /** @hidden */
    @property({ visible: false, override: true })
    protected _audioSource: cc.AudioSource = null;

    /** The array of [[cc.AudioSource]]s to be triggered. */
    @property({ type: [cc.AudioSource], visible: true })
    protected _audioSources: cc.AudioSource[] = [];

    /** The array of [[AudioTriggerGroup]]s to be triggered. */
    @property({ type: [AudioTriggerGroup], visible: true })
    protected _audioGroups: AudioTriggerGroup[] = [];

    private _sourceUuidVolumeMap: Map<string, number> = new Map();

    /** Whether or not any of the included [[cc.AudioSource]]s are currently playing. */
    public get isPlaying(): boolean
    {
        for (let source of this._audioSources)
        {
            if (source.isPlaying) { return true; }
        }

        return false;
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        if (this._audioSources.length === 0)
        {
            CLog.warn("No audio sources provided.", LogTypes.Audio, this);
        }
    }

    /** Stops the associated [[cc.AudioSource]]s if it is playing. */
    public Stop(): void
    {
        this._audioSources.forEach(source => source.stop());
        this._audioGroups.forEach(group => group.Stop());
    }

    /** Pauses the associated [[cc.AudioSource]]s. */
    public Pause(): void
    {
        this._audioSources.forEach(source => source.pause());
        this._audioGroups.forEach(group => group.Pause());
        this.isPaused = true;
    }

    /** Resumes the associated [[cc.AudioSource]]s. */
    public Resume(): void
    {
        this._audioSources.forEach(source => source.resume());
        this._audioGroups.forEach(group => group.Resume());
        this.isPaused = false;
    }

    /** Plays all associated [[cc.AudioSource]]s. */
    public Trigger(): void
    {
        if ((!this._audioSources && !this._audioGroups)
            || (this._audioSources.length < 1 && this._audioGroups.length < 1)) { return; }

        for (let source of this._audioSources)
        {
            if (source.isPlaying)
            {
                if (this.noInterrupt) { continue; }
                source.stop();
            }
            this.playAudioSource(source);
        }
        for (let group of this._audioGroups)
        {
            if (group.isPlaying && this.noInterrupt) { continue; }
            group.Trigger();
        }

        this.isStarted = true;
    }

    /**
     * Sets volume to percent.
     * @param percent scaled volume to set to.
     */
    public SetVolume(percent: number): void
    {
        if (!this._audioSources || this._audioSources.length < 1) { return; }
        this._audioSources.forEach(audioSource =>
            {
                audioSource.volume = (this._sourceUuidVolumeMap.get(audioSource.uuid) * percent) || 0;
            }, this);
    }

    /**
     * Sets initial start volume from sources.
     */
    protected configureStartVolume(): void
    {
        if (!this._audioSources || this._audioSources.length < 1) { return; }
        this._audioSources.forEach(audioSource =>
            {
                this._sourceUuidVolumeMap.set(audioSource.uuid, audioSource.volume);
            }, this);
    }
}
